import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Pagination, StarRating, BackButton, Spinner } from '../../../components/common';
import SEO from '../../../components/common/SEO';
import { reviewService } from '../../../api/services';
import { makeServiceLabels } from '../../../constants/serviceLabels';
import { useDeepLTranslated } from '../../../hooks/useDeepLTranslated';

export default function Reviews() {
  const { t } = useTranslation();
  const maskName = (name) => {
    if (!name || name.length === 0) return t('info.reviews_anonymous');
    if (name.length === 1) return name + '**';
    return name.charAt(0) + '*'.repeat(Math.max(name.length - 1, 2));
  };
  const [currentPage, setCurrentPage] = useState(1);
  const [reviewsData, setReviewsData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [averageRating, setAverageRating] = useState(0);
  const [sortOrder, setSortOrder] = useState('latest');
  const [sortOpen, setSortOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const sortRef = useRef(null);

  // 후기 본문/차량명/서비스 라벨을 현재 UI 언어로 번역 — 한국어로 작성된 후기도 다국어 페이지에서 자동 번역
  const reviewTextsBundle = useMemo(() => {
    const out = [];
    reviewsData.forEach((r) => {
      out.push(r.text || '', r.vehicle || '', r.service || '');
    });
    return out;
  }, [reviewsData]);
  const tReviewTexts = useDeepLTranslated(reviewTextsBundle);

  useEffect(() => {
    if (!sortOpen) return;
    const handleClick = (e) => { if (!sortRef.current?.contains(e.target)) setSortOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [sortOpen]);

  useEffect(() => {
    reviewService.getStats().then((res) => {
      setAverageRating(res.average_rating || 0);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const serviceLabels = makeServiceLabels(t);
    reviewService.getList({ page: currentPage, size: 10, sort: sortOrder }).then((res) => {
      setReviewsData((res.items || []).map((r) => ({
        id: r.id,
        name: maskName(r.username),
        service: (r.service_type && serviceLabels[r.service_type]) || r.service_label || t('nav.private_transfer'),
        vehicle: r.vehicle_label || '',
        rating: r.rating,
        text: r.content,
        date: r.created_at ? new Date(r.created_at).toLocaleDateString('ko-KR') : '',
      })));
      setTotalPages(res.pages || 1);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [currentPage, sortOrder]);

  return (
    <div className="bg-white min-h-screen">
      <SEO route="/reviews" />
      <div className="max-w-content mx-auto px-2.5 sm:px-5 py-8 sm:py-12">
        {/* Title */}
        <div className="flex items-center gap-3 mb-6">
          <BackButton />
          <h1 className="text-xl font-bold text-mono-900">{t('info.reviews_title')}</h1>
        </div>

        {/* Rating + Sort row */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <StarRating rating={Math.round(averageRating)} size={20} />
            <span className="text-sm font-bold text-mono-900">{t('info.reviews_avg')} {averageRating}</span>
          </div>
          <div className="relative" ref={sortRef}>
            <button
              type="button"
              onClick={() => setSortOpen((v) => !v)}
              className={`flex items-center gap-1 h-8 px-3 text-xs font-medium bg-white border rounded-lg transition ${sortOpen ? 'border-orange-500 text-orange-500' : 'border-mono-200 text-mono-900 hover:border-mono-300'}`}
            >
              {sortOrder === 'latest' ? t('info.reviews_sort_latest') : t('info.reviews_sort_rating')}
              <Icon name="chevron-down" size={12} className={`transition-transform duration-200 ${sortOpen ? 'rotate-180 text-orange-500' : 'text-mono-400'}`} />
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-full mt-1.5 bg-white border border-mono-200 rounded-xl shadow-lg overflow-hidden z-50 min-w-[100px]">
                {[{ value: 'latest', label: t('info.reviews_sort_latest') }, { value: 'rating', label: t('info.reviews_sort_rating') }].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => { setSortOrder(opt.value); setCurrentPage(1); setSortOpen(false); }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${sortOrder === opt.value ? 'bg-orange-50 text-orange-500 font-semibold' : 'text-mono-900 hover:bg-orange-50/50'}`}
                  >
                    {opt.label}
                    {sortOrder === opt.value && <Icon name="check" size={14} className="text-orange-500" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Review List */}
        {loading && <div className="flex justify-center py-8"><Spinner /></div>}
        <div className="divide-y divide-bg-divider-light">
          {!loading && reviewsData.length === 0 && (
            <div className="py-16 text-center"><p className="text-sm text-mono-400">{t('info.reviews_no')}</p></div>
          )}
          {!loading && reviewsData.map((review, i) => {
            const tText = tReviewTexts[i * 3] || review.text;
            const tVehicle = tReviewTexts[i * 3 + 1] || review.vehicle;
            const tService = tReviewTexts[i * 3 + 2] || review.service;
            return (
              <div key={review.id} className="py-6">
                {/* Author row */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-mono-100 flex items-center justify-center">
                    <Icon name="person" size={20} className="text-mono-400" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-mono-900">{review.name}</p>
                    <p className="text-2sm text-mono-400">{review.date}</p>
                  </div>
                </div>

                {/* Service info */}
                <div className="mb-3 space-y-1">
                  <p className="text-2sm text-mono-500">
                    <span className="text-mono-400">{t('info.reviews_used_service')}: </span>
                    {tService}
                  </p>
                  <p className="text-2sm text-mono-500">{tVehicle}</p>
                </div>

                {/* Star rating */}
                <div className="mb-3">
                  <StarRating rating={review.rating} />
                </div>

                {/* Review text */}
                <p className="text-sm text-mono-500 leading-relaxed whitespace-pre-line break-keep">
                  {tText}
                </p>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        <Pagination
          current={currentPage}
          total={totalPages}
          onChange={setCurrentPage}
          className="mt-8"
        />
      </div>
    </div>
  );
}
